import type { Location } from "history";
import { useEffect, useState } from "react";
import { t } from "ttag";
import _ from "underscore";

import { useTempStorage } from "metabase/common/hooks";
import { usePageTitle } from "metabase/hooks/use-page-title";
import type { AuthProvider } from "metabase/plugins/types";
import { useSelector } from "metabase/redux";
import { getApplicationName } from "metabase/selectors/whitelabel";
import { Box, Divider } from "metabase/ui";

import { getAuthProviders } from "../../selectors";
import type { LoginData } from "../../types";
import { AuthLayout } from "../AuthLayout";
import { PasswordPanel } from "../PasswordPanel";

interface LoginQueryString {
  redirect?: string;
  u?: string;
  p?: string;
}

interface LoginQueryParams {
  provider?: string;
}

interface LoginProps {
  params?: LoginQueryParams;
  location?: Location<LoginQueryString>;
}

type AutoLoginSource = "query" | "pending_redirect";

interface AutoLoginValues {
  username: string;
  password: string;
}

interface AutoLoginCredentials {
  source: AutoLoginSource;
  values: AutoLoginValues;
}

export const Login = ({ params, location }: LoginProps): JSX.Element => {
  const providers = useSelector(getAuthProviders);
  const queryAutoLoginCredentials = getQueryAutoLoginCredentials(location?.query);
  const [pendingRedirectCredentials, setPendingRedirectCredentials] =
    useTempStorage("pending-login-credentials");
  const [consumedRedirectCredentials, setConsumedRedirectCredentials] =
    useState<AutoLoginValues | null>(null);
  const autoLoginCredentials =
    queryAutoLoginCredentials ??
    getPendingRedirectAutoLoginCredentials(
      consumedRedirectCredentials,
      pendingRedirectCredentials,
    );
  const selection = getSelectedProvider(
    providers,
    autoLoginCredentials ? "password" : params?.provider,
  );
  const redirectUrl = location?.query?.redirect;
  const applicationName = useSelector(getApplicationName);

  usePageTitle(t`Login`);

  useEffect(() => {
    if (!pendingRedirectCredentials) {
      return;
    }

    if (!queryAutoLoginCredentials) {
      setConsumedRedirectCredentials(pendingRedirectCredentials);
    }

    setPendingRedirectCredentials(undefined);
  }, [
    pendingRedirectCredentials,
    queryAutoLoginCredentials,
    setPendingRedirectCredentials,
  ]);

  const [passwordProvider, otherProviders] = _.partition(
    providers,
    (provider) => provider.name === "password",
  );
  return (
    <AuthLayout>
      <Box
        role="heading"
        c="text-primary"
        fz="1.25rem"
        fw="bold"
        lh="1.5rem"
        ta="center"
      >
        {t`Sign in to ${applicationName}`}
      </Box>
      {selection?.name === "password" && (
        <Box mt="2.5rem">
          <PasswordPanel
            redirectUrl={redirectUrl}
            initialValues={autoLoginCredentials?.values}
            location={location}
            shouldAutoSubmit={Boolean(autoLoginCredentials)}
          />
        </Box>
      )}
      {selection && selection.Panel && selection.name !== "password" && (
        <Box mt="2.5rem">
          <selection.Panel redirectUrl={redirectUrl} />
        </Box>
      )}
      {!selection && (
        <Box mt="3.5rem">
          {otherProviders.map((provider) => (
            <Box key={provider.name} mt="2rem" ta="center">
              <provider.Button isCard={true} redirectUrl={redirectUrl} />
            </Box>
          ))}
          {passwordProvider.map((provider) => (
            <>
              <Divider mt="2rem" />
              <Box key={provider.name} mt="1rem" ta="center">
                <provider.Button isCard={true} redirectUrl={redirectUrl} />
              </Box>
            </>
          ))}
        </Box>
      )}
    </AuthLayout>
  );
};

const getSelectedProvider = (
  providers: AuthProvider[],
  providerName?: string,
): AuthProvider | undefined => {
  const provider =
    providers.length > 1
      ? providers.find((p) => p.name === providerName)
      : providers[0];

  return provider?.Panel ? provider : undefined;
};

const getQueryAutoLoginCredentials = (
  query?: LoginQueryString,
): AutoLoginCredentials | null => {
  if (
    typeof query?.u !== "string" ||
    typeof query?.p !== "string" ||
    query.u === "" ||
    query.p === ""
  ) {
    return null;
  }

  return {
    source: "query",
    values: {
      username: query.u,
      password: query.p,
    },
  };
};

const getPendingRedirectAutoLoginCredentials = (
  consumedRedirectCredentials: AutoLoginValues | null,
  pendingRedirectCredentials: AutoLoginValues | undefined,
): AutoLoginCredentials | null => {
  const credentials =
    consumedRedirectCredentials ?? pendingRedirectCredentials ?? null;

  if (!credentials) {
    return null;
  }

  return {
    source: "pending_redirect",
    values: credentials,
  };
};
